import isEqual from 'lodash/isEqual';

import type {
  FreeTextFilter,
  GroupFilter,
  ListFilter,
  RangeFilter,
  SchoolFilter,
  SingleValueFilter,
  StaticListFilter,
  TreeFilter,
} from '../types/filters';
import type { FilterHandler } from './types';

export const listFilterHandler: FilterHandler<ListFilter> = {
  isActive: (state) => state.selections.length > 0,
  isDefault: (state) => state.selections.length === 0,
  getEffectiveFilter: (state) => ({ ...state }),
  getRequiredSelectionListIds: (definition) => [definition.selectionListId],
};

export const treeFilterHandler: FilterHandler<TreeFilter> = {
  isActive: (state) => state.selections.length > 0,
  isDefault: (state) => state.selections.length === 0,
  getEffectiveFilter: (state) => ({ ...state }),
  getRequiredSelectionListIds: (definition) => definition.selectionListIds,
};

export const staticListFilterHandler: FilterHandler<StaticListFilter> = {
  isActive: (state) => state.selections.length > 0,
  isDefault: (state) => state.selections.length === 0,
  getEffectiveFilter: (state) => ({ ...state }),
};

export const freeTextFilterHandler: FilterHandler<FreeTextFilter> = {
  isActive: (state) => state.text.length > 0,
  isDefault: (state, definition) =>
    state.text.length === definition.text.length,
  getEffectiveFilter: (state) => ({ ...state }),
};

export const schoolFilterHandler: FilterHandler<SchoolFilter> = {
  isActive: (state) => Object.keys(state.schoolResultItems).length > 0,
  isDefault: (state) => Object.keys(state.schoolResultItems).length === 0,
  getEffectiveFilter: (state) => ({ ...state }),
};

export const groupFilterHandler: FilterHandler<GroupFilter> = {
  isActive: () => false,
  isDefault: () => true,
  getEffectiveFilter: (state) => ({ ...state }),
};

export const singleValueFilterHandler = <T>(): FilterHandler<
  SingleValueFilter<T>
> => ({
  isActive: (state) => state.value !== null && state.value !== undefined,
  isDefault: (state, definition) => isEqual(state.value, definition.value),
  getEffectiveFilter: (state) => ({ ...state }),
});

export const passThroughEffectiveFilter = <F>(state: F): F => state;

const hasNoBounds = (state: RangeFilter) =>
  state.min === undefined && state.max === undefined;

export const rangeWithDefaultsWhenEmpty = (
  defaultMin: number,
  defaultMax: number
): FilterHandler<RangeFilter> => ({
  isActive: (state) => !hasNoBounds(state),
  isDefault: hasNoBounds,
  getEffectiveFilter: (state) =>
    hasNoBounds(state) ? { ...state, min: defaultMin, max: defaultMax } : state,
});

export const rangeWithFallbackBounds = (
  defaultMin: number,
  defaultMax: number
): FilterHandler<RangeFilter> => ({
  isActive: (state) => !hasNoBounds(state),
  isDefault: hasNoBounds,
  getEffectiveFilter: (state) => ({
    ...state,
    min: state.min ?? defaultMin,
    max: state.max ?? defaultMax,
  }),
});

export const staticListWithDefaultSelection = (option: {
  value: string;
  label: string;
  limit: number;
}): Partial<FilterHandler<StaticListFilter>> => ({
  isActive: () => true,
  getEffectiveFilter(state, definition) {
    if (state.selections.length > 0) {
      return { ...state, isVisible: true, isUserSubmitted: true };
    }

    return {
      ...definition,
      selections: [
        { id: option.value, name: option.label, limit: option.limit },
      ],
      isVisible: false,
      isUserSubmitted: false,
    };
  },
});
